import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface JoinRequest {
  id: string;
  pondId: string;
  userId: string;
  status: string;
  createdAt: string;
  userName?: string;
  pondName?: string;
}

export const useJoinRequests = (pondId: string | null) => {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchRequests = async () => {
    if (!user || !pondId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    try {
      // First check if user is the owner
      const { data: pond } = await supabase
        .from("ponds")
        .select("owner_id")
        .eq("id", pondId)
        .single();

      if (pond?.owner_id !== user.id) {
        setRequests([]);
        setLoading(false);
        return;
      }

      // Fetch pending requests
      const { data, error } = await supabase
        .from("pond_join_requests")
        .select("*")
        .eq("pond_id", pondId)
        .eq("status", "pending");

      if (error) throw error;

      // Fetch user names
      const userIds = data?.map((r) => r.user_id) || [];
      let profilesMap: Record<string, string> = {};
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, name")
          .in("user_id", userIds);
        
        profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.user_id] = p.name;
          return acc;
        }, {} as Record<string, string>);
      }

      setRequests(
        (data || []).map((r) => ({
          id: r.id,
          pondId: r.pond_id,
          userId: r.user_id,
          status: r.status,
          createdAt: r.created_at,
          userName: profilesMap[r.user_id] || "Usuário",
        }))
      );
    } catch (error) {
      console.error("Error fetching join requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [user, pondId]);

  const approveRequest = async (requestId: string) => {
    try {
      const request = requests.find((r) => r.id === requestId);
      if (!request) return false;

      // Update request status
      const { error: updateError } = await supabase
        .from("pond_join_requests")
        .update({ status: "approved" })
        .eq("id", requestId);

      if (updateError) throw updateError;

      // Add user as member
      const { error: memberError } = await supabase
        .from("pond_members")
        .insert({
          pond_id: request.pondId,
          user_id: request.userId,
          role: "member",
        });

      if (memberError) throw memberError;

      toast.success("Solicitação aprovada! 🐸");
      await fetchRequests();
      return true;
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error("Erro ao aprovar solicitação");
      return false;
    }
  };

  const rejectRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from("pond_join_requests")
        .update({ status: "rejected" })
        .eq("id", requestId);

      if (error) throw error;

      toast.success("Solicitação rejeitada");
      await fetchRequests();
      return true;
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error("Erro ao rejeitar solicitação");
      return false;
    }
  };

  return {
    requests,
    loading,
    approveRequest,
    rejectRequest,
    refetch: fetchRequests,
  };
};
